import React, { useState } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SEO from "../components/SEO";
import SecurityNotice from "../components/SecurityNotice";

function IntegrationExamples() {
  const [showModal, setShowModal] = useState(false);
  const [csrResult, setCsrResult] = useState(null);
  // 2026-07-04 operator consistency directive : demo failures used to
  // surface via raw browser alert() (the only live window.alert sites
  // left on the site) ; they now render the shared dismissible Alert
  // below the page intro, matching every tool page's error mechanism.
  const [demoError, setDemoError] = useState(null);
  const [demoData, setDemoData] = useState({
    commonName: "",
    organization: "",
    email: "",
  });

  // Use production URL for examples display, actual origin for demos
  const exampleOrigin = "https://tools.trustico.com";
  const actualOrigin = window.location.origin;

  // Demo functions
  const openCSRPopupDemo = () => {
    const popup = window.open(
      `${actualOrigin}/csr/popup`,
      "csr-popup",
      "width=1000,height=800,toolbar=no,menubar=no"
    );
    if (!popup) {
      setDemoError(
        "The popup was blocked. Please allow popups for this site to see the demo."
      );
    }
  };

  const openModalDemo = () => {
    setShowModal(true);
  };

  const closeModalDemo = () => {
    setShowModal(false);
  };

  const openWithDataDemo = () => {
    const commonName = demoData.commonName || "www.example.com";
    const org = demoData.organization || "Example Corp";
    const email = demoData.email || "admin@example.com";

    const popup = window.open(
      `${actualOrigin}/csr/popup`,
      "csr-popup",
      "width=1000,height=800"
    );
    if (!popup) {
      setDemoError(
        "The popup was blocked. Please allow popups for this site to see the demo."
      );
      return;
    }

    // Listen for ready signal first
    const handler = (e) => {
      if (e.origin !== actualOrigin) return;

      if (e.data.type === "csr-popup-ready") {
        // Send data once when popup is ready
        popup.postMessage(
          {
            type: "init-csr-popup",
            commonName,
            organization: org,
            email: email,
          },
          actualOrigin
        );

        // Clean up
        window.removeEventListener("message", handler);
      }
    };
    window.addEventListener("message", handler);

    // Also try sending immediately in case popup is already loaded
    setTimeout(() => {
      try {
        popup.postMessage(
          {
            type: "init-csr-popup",
            commonName,
            organization: org,
            email: email,
          },
          actualOrigin
        );
      } catch {
        // Silent fail if popup was closed
      }
    }, 100);
  };

  const openAndListenDemo = () => {
    const popup = window.open(
      `${actualOrigin}/csr/popup`,
      "csr-popup",
      "width=1000,height=800"
    );
    if (!popup) {
      setDemoError(
        "The popup was blocked. Please allow popups for this site to see the demo."
      );
      return;
    }

    // Listen for results
    const handler = (event) => {
      if (event.origin !== actualOrigin) return;

      switch (event.data.type) {
        case "csr-generated":
          setCsrResult({
            commonName: event.data.commonName,
            csr: event.data.csr,
          });
          break;

        case "csr-error":
          setDemoError("The CSR could not be generated. " + event.data.error);
          break;
      }
    };
    window.addEventListener("message", handler);
  };

  // Handle modal close messages
  React.useEffect(() => {
    const handler = (event) => {
      if (event.data.type === "close-csr-popup") {
        closeModalDemo();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 2026-07-04 tools-wide overhaul (operator-approved) : canonicalUrl
          added so search engines index this guide at its one true address.
          Casing swept the same day for audit item 7, and again on
          2026-08-27 : the popup-blocked / CSR-error alerts are plain
          sentences, as every customer-facing error now is. */}
      <SEO
        title="CSR Popup Integration Examples"
        description="Learn how to integrate the Trustico® SSL Certificate CSR generator popup into your website. Multiple integration methods with code examples."
        keywords="CSR popup integration, SSL Certificate generator, embed CSR tool, CSR API alternative"
        canonicalUrl="https://tools.trustico.com/csr-integration"
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          SSL Certificate CSR Popup Integration Examples
        </h1>
        <p className="text-body">
          The Trustico® CSR Popup tool can be integrated into any website using
          various methods. Below are examples for different use cases. When
          implementing, always verify the origin when using postMessage for
          security. The private key is generated client-side and never sent to
          our servers.
        </p>
      </div>

      <SecurityNotice />

      {demoError && (
        <Alert
          type="error"
          title="Demo Error"
          className="mb-6"
          onClose={() => setDemoError(null)}
        >
          {demoError}
        </Alert>
      )}

      {/* Example 1: Direct Link.
          2026-07-04 operator consistency directive : the five Method
          headings used className="h2", a class that exists nowhere in
          src/client/styles/index.css, so Tailwind's preflight reset
          left them rendering as PLAIN unstyled text (the "incorrect
          sized titles" failure mode). They now carry the reference
          section heading treatment (text-lg font-semibold), and the
          "Demo :" sub-headings below them are the standard
          text-base font-semibold sub-heading tier, retitled "Live
          Demo" without the trailing colon no other heading in the
          suite carries, each with the explanatory sentence every
          heading requires. */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-heading mb-2">
          Method 1 : Direct Link
        </h2>
        <p className="text-body mb-4">
          The simplest integration - just link to the CSR popup page.
        </p>

        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg font-mono mb-4">
          <pre>{`<a href="${exampleOrigin}/csr/popup" target="_blank">
  Generate SSL Certificate CSR
</a>`}</pre>
        </div>

        <h3 className="text-base font-semibold text-heading mb-2">Live Demo</h3>
        <p className="text-body mb-4">
          Click the link below to open the CSR popup page in a new browser tab.
        </p>
        <a
          href={`${actualOrigin}/csr/popup`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Generate SSL Certificate CSR
          <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />
        </a>
      </div>

      {/* Example 2: Popup Window - heading treatment per the Method 1
          comment above (2026-07-04 operator consistency directive). */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-heading mb-2">
          Method 2 : Popup Window
        </h2>
        <p className="text-body mb-4">
          Open the CSR generator in a popup window with optimal size.
        </p>

        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg font-mono mb-4">
          <pre>{`<button onclick="openCSRPopup()">
  Generate SSL Certificate CSR
</button>

<script>
function openCSRPopup() {
  window.open(
    '${exampleOrigin}/csr/popup',
    'csr-popup',
    'width=1000,height=800,toolbar=no,menubar=no'
  );
}
</script>`}</pre>
        </div>

        <h3 className="text-base font-semibold text-heading mb-2">Live Demo</h3>
        <p className="text-body mb-4">
          Click the button below to open the CSR generator in a popup window.
        </p>
        <button
          onClick={openCSRPopupDemo}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Generate SSL Certificate CSR (Popup)
        </button>
      </div>

      {/* Example 3: Modal - heading treatment per the Method 1 comment
          above (2026-07-04 operator consistency directive). */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-heading mb-2">
          Method 3 : Modal with Iframe
        </h2>
        <p className="text-body mb-4">
          Embed the CSR generator in a modal overlay on your page.
        </p>

        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg font-mono mb-4 overflow-x-auto">
          <pre>{`<!-- Modal CSS -->
<style>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  width: 1000px;
  height: 850px;
  max-width: 95%;
  max-height: 90vh;
  border-radius: 8px;
  overflow: hidden;
}
</style>

<!-- Modal HTML -->
<div id="csrModal" class="modal" style="display: none;">
  <div class="modal-content">
    <iframe
      src="${exampleOrigin}/csr/popup"
      style="width: 100%; height: 100%; border: none;"
    ></iframe>
  </div>
</div>`}</pre>
        </div>

        <h3 className="text-base font-semibold text-heading mb-2">Live Demo</h3>
        <p className="text-body mb-4">
          Click the button below to open the CSR generator in a modal overlay on
          this page.
        </p>
        <button
          onClick={openModalDemo}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Generate SSL Certificate CSR (Modal)
        </button>
      </div>

      {/* Example 4: Pre-filled Data - heading treatment per the
          Method 1 comment above (2026-07-04 operator consistency
          directive). */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-heading mb-2">
          Method 4 : Pre-filled Data via PostMessage
        </h2>
        <p className="text-body mb-4">
          Pass customer data to pre-fill the CSR form using postMessage API.
        </p>

        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg font-mono mb-4 overflow-x-auto">
          <pre>{`function openCSRWithData() {
  const popup = window.open(
    '${exampleOrigin}/csr/popup',
    'csr-popup',
    'width=1000,height=800'
  );

  // Listen for ready signal
  window.addEventListener('message', function(event) {
    if (event.origin !== '${exampleOrigin}') return;

    if (event.data.type === 'csr-popup-ready') {
      // Send data when popup is ready
      popup.postMessage({
        type: 'init-csr-popup',
        commonName: 'www.example.com',
        organization: 'Example Corp',
        email: 'admin@example.com'
      }, '${exampleOrigin}');
    }
  });
}`}</pre>
        </div>

        <h3 className="text-base font-semibold text-heading mb-2">Live Demo</h3>
        <p className="text-body mb-4">
          Fill in any of the fields below, then open the popup to see the values
          arrive pre-filled.
        </p>
        {/* 2026-07-04 operator consistency directive : placeholders are
            realistic example values, never label-style prose ; the old
            label duty moves to aria-label so each input keeps an
            accessible name. */}
        <div className="space-y-2 mb-4">
          <input
            type="text"
            aria-label="Common Name"
            placeholder="www.example.com"
            value={demoData.commonName}
            onChange={(e) =>
              setDemoData({ ...demoData, commonName: e.target.value })
            }
            className="input"
          />
          <input
            type="text"
            aria-label="Organization"
            placeholder="Example Corp"
            value={demoData.organization}
            onChange={(e) =>
              setDemoData({ ...demoData, organization: e.target.value })
            }
            className="input"
          />
          <input
            type="email"
            aria-label="E-Mail Address"
            placeholder="admin@example.com"
            value={demoData.email}
            onChange={(e) =>
              setDemoData({ ...demoData, email: e.target.value })
            }
            className="input"
          />
        </div>
        <button
          onClick={openWithDataDemo}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Generate with Pre-filled Data
        </button>
      </div>

      {/* Example 5: Receive Data - heading treatment per the Method 1
          comment above (2026-07-04 operator consistency directive). */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-heading mb-2">
          Method 5 : Receive Generated CSR Data
        </h2>
        <p className="text-body mb-4">
          Listen for the generated CSR and private key data from the popup.
        </p>

        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg font-mono mb-4 overflow-x-auto">
          <pre>{`window.addEventListener('message', function(event) {
  // Verify origin for security
  if (event.origin !== '${exampleOrigin}') {
    return;
  }

  switch (event.data.type) {
    case 'csr-generated':
      // Use the CSR in your application
      break;

    case 'csr-error':
      alert('ERROR : ' + event.data.error);
      break;
  }
});`}</pre>
        </div>

        <h3 className="text-base font-semibold text-heading mb-2">Live Demo</h3>
        <p className="text-body mb-4">
          Open the popup and generate a CSR : the received data will appear
          below.
        </p>
        <button
          onClick={openAndListenDemo}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mb-4"
        >
          Generate and Receive CSR Data
        </button>

        {csrResult && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 p-4 rounded-lg">
            {/* Sub-heading normalized to the standard sub-heading tier and the
                trailing " :" dropped - no other heading in the suite
                carries one (2026-07-04 operator consistency directive). */}
            <h3 className="text-base font-semibold text-heading mb-2">
              Generated CSR Data
            </h3>
            <p className="mb-2">
              <strong>Common Name :</strong> {csrResult.commonName}
            </p>
            <p className="mb-2">
              <strong>CSR :</strong>
            </p>
            {/* The shared PEM treatment, matching the CSR box in
                CSRPopup.jsx - this panel demonstrates what that popup
                returns, so it should look like it. The hand-rolled classes
                it replaces bypassed the shared textarea entirely : no
                control-token border, no dark fill, no focus ring. The
                aria-label is there because the "CSR :" line above is a
                paragraph, not a label element. */}
            <textarea
              value={csrResult.csr}
              readOnly
              aria-label="Certificate Signing Request (CSR)"
              className="textarea certificate-textarea"
              rows={8}
            />
            <p className="text-body mt-2">
              Private key is also received but not displayed here for security.
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModalDemo();
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg w-[1000px] h-[850px] max-w-[95%] max-h-[90vh] relative">
            <iframe
              src={`${actualOrigin}/csr/popup`}
              className="w-full h-full rounded-lg"
              title="CSR Generator"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default IntegrationExamples;
